import { sourceLabel, sourceProvider, suggestTag, taggedAddress } from '../../lib/mail.js'

const Fez = window.Fez;
if (!Fez) throw new Error('fez runtime not loaded: load fez (window.Fez) before component modules');

// --- mail-sources-dialog ---
let __fez_component_0;
{

__fez_component_0 = class {
  PROPS = {
    container: { type: String, required: true },
    mailbox: { type: String, required: true },
    addresses: Array,
    can_edit: Boolean,
    view: { type: String, default: 'sources', enum: ['sources', 'forwarding'] },
  }

  init() {
    this.state.loading = true
    this.state.error = ''
    this.state.items = []
    this.state.form = null
    this.state.errors = {}
    this.state.busy = ''
    this.state.deleting = ''
    this.state.guide = ''
    this.state.changed = false
    this.state.view = this.props.view
  }

  show(view) {
    this.state.view = view
  }

  forwardingChanged() {
    this.state.changed = true
  }

  onMount() {
    this.load()
  }

  async load() {
    try {
      const list = await window.App.api(`container/${this.props.container}/sources`, { mailbox: this.props.mailbox }, { silent: true })
      this.state.items = list.items
      if (!list.items.length && this.props.can_edit) this.add()
    } catch (error) {
      this.state.error = error.message
    } finally {
      this.state.loading = false
    }
  }

  add() {
    this.state.errors = {}
    this.state.form = { ref: '', key: 'new', address: '', name: '', tag: '' }
  }

  edit(ref) {
    const item = this.state.items.find((source) => source.ref === ref)
    if (!item) return
    this.state.errors = {}
    this.state.form = { ref, key: ref, address: item.address || item.forwarder || '', name: item.name, tag: item.tag || '' }
  }

  cancel() {
    this.state.form = null
  }

  // A new address proposes the provider's name and tag, unless they were typed already.
  suggest(event) {
    if (event.target.name !== 'address') return
    const address = (event.target.value || '').trim()
    const form = this.state.form
    if (!form || form.ref || !address.includes('@')) return
    const names = this.find('input[name="name"]')
    const tags = this.find('input[name="tag"]')
    const provider = sourceProvider(address)
    if (names && !names.value) names.value = provider.key === 'other' ? '' : provider.name
    if (tags && !tags.value) tags.value = suggestTag(address)
  }

  async save(event) {
    event.preventDefault()
    const form = this.formData(event.target)
    const ref = this.state.form.ref
    this.state.busy = 'save'
    try {
      const saved = await window.App.api(`container/${this.props.container}/source_save`, {
        mailbox: this.props.mailbox,
        source: ref || undefined,
        address: ref ? undefined : (form.address || '').trim(),
        name: (form.name || '').trim() || undefined,
        tag: (form.tag || '').trim().toLowerCase(),
      })
      this.state.items = ref ? this.state.items.map((item) => (item.ref === ref ? saved : item)) : [...this.state.items, saved]
      this.state.form = null
      this.state.changed = true
      if (!ref) this.state.guide = saved.ref
    } catch (error) {
      this.state.errors = error.details || {}
    } finally {
      this.state.busy = ''
    }
  }

  // Keeps a detected source as it is, or drops a provider's request once it is handled.
  async settle(ref, dismiss) {
    this.state.busy = ref
    try {
      const saved = await window.App.api(`container/${this.props.container}/source_save`, { mailbox: this.props.mailbox, source: ref, dismiss_confirm: dismiss })
      this.state.items = this.state.items.map((item) => (item.ref === ref ? saved : item))
      this.state.changed = true
    } finally {
      this.state.busy = ''
    }
  }

  askDelete(ref) {
    this.state.deleting = ref
  }

  async remove(ref) {
    this.state.busy = ref
    try {
      await window.App.api(`container/${this.props.container}/source_delete`, { mailbox: this.props.mailbox, source: ref })
      this.state.items = this.state.items.filter((item) => item.ref !== ref)
      this.state.changed = true
    } finally {
      this.state.busy = ''
      this.state.deleting = ''
    }
  }

  toggleGuide(ref) {
    this.state.guide = this.state.guide === ref ? '' : ref
  }

  done() {
    window.Dialog.close(this.state.changed ? true : undefined)
  }

  beforeRender() {
    const home = this.props.addresses[0] || ''
    this.state.home = home
    this.state.rows = this.state.items.map((item) => {
      const provider = sourceProvider(item.address)
      const to = taggedAddress(home, item.tag)
      return {
        ref: item.ref,
        label: sourceLabel(item),
        account: item.address && item.address !== sourceLabel(item) ? item.address : item.address ? '' : `An account forwarded by ${item.forwarder}`,
        icon: provider.icon,
        color: item.color,
        detected: item.status === 'detected',
        confirm: item.confirm,
        confirmText: item.confirm ? `${item.confirm.provider === 'gmail' ? 'Gmail' : 'The provider'} asks to confirm forwarding from ${item.confirm.address}.` : '',
        to,
        steps: provider.steps.map((step) => step.replaceAll('{to}', to)),
        tag: item.tag ? `+${item.tag}` : '',
        guide: this.state.guide === item.ref,
        busy: this.state.busy === item.ref,
        deleting: this.state.deleting === item.ref,
      }
    })
    const form = this.state.form
    this.state.tagHint = home ? `Mail to ${taggedAddress(home, 'tag')} is always this source's, whatever its headers say. Leave empty to rely on the headers.` : ''
    this.state.formTitle = form && form.ref ? 'Edit source' : 'Add a source'
  }

  CSS = `:fez {
display:block;.src-views{display:inline-flex;gap:2px;margin-bottom:var(--space-4);padding:2px;border-radius:var(--radius-md);background:var(--color-bg-subtle)}.src-view{display:inline-flex;align-items:center;gap:var(--space-2);padding:var(--space-1) var(--space-3);border:0;border-radius:var(--radius-sm);background:transparent;color:var(--color-text-muted);font:inherit;font-size:var(--text-sm);font-weight:var(--weight-medium);cursor:pointer;&.active{background:var(--color-bg-raised);color:var(--color-text);box-shadow:var(--shadow-sm)}}.lead{margin-bottom:var(--space-4);color:var(--color-text-muted);font-size:var(--text-sm)}.src-list{display:grid;gap:var(--space-3)}.src-item{display:grid;gap:var(--space-3);padding:var(--space-3) var(--space-4);border:1px solid var(--color-border);border-radius:var(--radius-md)}.src-head{display:flex;align-items:center;gap:var(--space-3)}.src-icon{display:inline-grid;place-items:center;width:32px;height:32px;flex:none;border-radius:var(--radius-full);background:var(--color-bg-subtle)}.src-text{display:flex;flex-direction:column;flex:1;min-width:0;line-height:var(--leading-tight)}.src-name,.src-account{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.src-name{font-weight:var(--weight-semibold)}.src-account{color:var(--color-text-muted);font-size:var(--text-xs)}.src-note{display:flex;flex-wrap:wrap;align-items:center;gap:var(--space-2);padding:var(--space-3);border-radius:var(--radius-md);background:var(--color-accent-soft);font-size:var(--text-sm);&.warning{background:var(--color-warning-soft)}&.danger{background:var(--color-danger-soft)}}.src-note-text{flex:1;min-width:200px}.src-code{font-family:var(--font-mono);font-weight:var(--weight-semibold)}.src-steps{display:grid;gap:var(--space-2);margin:0;padding-left:var(--space-5);color:var(--color-text-muted);font-size:var(--text-sm)}.src-form{display:grid;gap:var(--space-4);margin-top:var(--space-4);padding:var(--space-4);border:1px solid var(--color-border);border-radius:var(--radius-md)}.src-form-title{margin:0;font-size:var(--text-md)}.src-form-actions{display:flex;justify-content:flex-end;gap:var(--space-2)}.src-add{margin-top:var(--space-4)}
}`

  HTML = `<div class="src-views" role="tablist">
<button type="button" class="src-view" class:active={state.view === 'sources'} role="tab" aria-selected={state.view === 'sources' ? 'true' : 'false'} onclick="fez.show('sources')"><ui-icon name="arrow-bend-down-right" size="16"></ui-icon>Mail coming in</button>
<button type="button" class="src-view" class:active={state.view === 'forwarding'} role="tab" aria-selected={state.view === 'forwarding' ? 'true' : 'false'} onclick="fez.show('forwarding')"><ui-icon name="arrow-bend-up-right" size="16"></ui-icon>Forwarding out</button>
</div>
{#if state.view === 'forwarding'}
<mailbox-forwarding container={props.container} mailbox={props.mailbox} :can_edit="props.can_edit" :on_change="() => fez.forwardingChanged()"></mailbox-forwarding>
<div class="dialog-actions">
<ui-button variant="primary" label="Done" onclick="fez.done()"></ui-button>
</div>
{:else if state.error}
<ui-empty icon="warning-circle" title="Could not load the sources" text={state.error}></ui-empty>
{:else}
<p class="lead">Forward mail from Gmail, Outlook or any other account to this mailbox and it keeps the account's name: a filter and a pill on every message it forwards. Accounts found in forwarding headers show up here on their own.</p>
{#if state.loading}
<ui-spinner></ui-spinner>
{:else if !state.rows.length && !state.form}
<ui-empty icon="arrow-bend-down-right" title="No sources yet" text="Nothing forwards into this mailbox so far."></ui-empty>
{/if}
<div class="src-list">
{#each state.rows as row}
<div class="src-item" key={row.ref}>
<div class="src-head">
<span class="src-icon" style="color: var(--color-avatar-{row.color})"><ui-icon name={row.icon} size="18"></ui-icon></span>
<span class="src-text">
<span class="src-name">{row.label}</span>
{#if row.account}<span class="src-account">{row.account}</span>{/if}
</span>
{#if row.detected}<ui-badge tone="accent" label="Detected"></ui-badge>{/if}
{#if row.tag}<ui-badge label={row.tag}></ui-badge>{/if}
<ui-button size="sm" variant="ghost" icon="question" title={row.guide ? 'Hide how to forward' : 'How to forward from it'} onclick="fez.toggleGuide('{row.ref}')"></ui-button>
{#if props.can_edit}
<ui-button size="sm" variant="ghost" icon="pencil-simple" title="Edit" onclick="fez.edit('{row.ref}')"></ui-button>
<ui-button size="sm" variant="ghost" icon="trash" title="Delete" onclick="fez.askDelete('{row.ref}')"></ui-button>
{/if}
</div>
{#if row.confirm}
<div class="src-note warning">
<span class="src-note-text">{row.confirmText}{#if row.confirm.code} Code: <span class="src-code">{row.confirm.code}</span>{/if}</span>
{#if row.confirm.url}<ui-button size="sm" icon="arrow-square-out" label="Open link" href={row.confirm.url} target="_blank" rel="noopener noreferrer"></ui-button>{/if}
{#if props.can_edit}<ui-button size="sm" variant="ghost" label="Done" loading={row.busy} onclick="fez.settle('{row.ref}', true)"></ui-button>{/if}
</div>
{/if}
{#if row.detected && props.can_edit}
<div class="src-note">
<span class="src-note-text">Found in the forwarding headers of mail to this mailbox. Keep it to name it, or delete it.</span>
<ui-button size="sm" label="Keep" loading={row.busy} onclick="fez.settle('{row.ref}', false)"></ui-button>
</div>
{/if}
{#if row.deleting}
<div class="src-note danger">
<span class="src-note-text">Its mail stays in the mailbox and only loses the source.</span>
<ui-button size="sm" variant="ghost" label="Cancel" onclick="fez.askDelete('')"></ui-button>
<ui-button size="sm" variant="danger" label="Delete source" loading={row.busy} onclick="fez.remove('{row.ref}')"></ui-button>
</div>
{/if}
{#if row.guide}
<ui-copy label="Forward to" value={row.to}></ui-copy>
<ol class="src-steps">
{#each row.steps as step}
<li>{step}</li>
{/each}
</ol>
{/if}
</div>
{/each}
</div>

{#if state.form}
{#key state.form.key}
<form class="src-form" onsubmit="fez.save(event)" onchange="fez.suggest(event)" novalidate>
<h3 class="src-form-title">{state.formTitle}</h3>
{#if !state.form.ref}
<ui-input name="address" label="Account" type="email" placeholder="you@gmail.com" hint="The outside address whose mail is forwarded here." error={state.errors.address} autofocus required></ui-input>
{/if}
<ui-input name="name" label="Name" value={state.form.name} placeholder="Gmail" hint="Shown in the sidebar and on its mail; the address when empty." error={state.errors.name}></ui-input>
<ui-input name="tag" label="Tag" value={state.form.tag} placeholder="gmail" hint={state.tagHint} error={state.errors.tag} mono></ui-input>
<div class="src-form-actions">
{#if state.rows.length || state.form.ref}<ui-button label="Cancel" onclick="fez.cancel()"></ui-button>{/if}
<ui-button type="submit" variant="primary" label={state.form.ref ? 'Save' : 'Add source'} loading={state.busy === 'save'}></ui-button>
</div>
</form>
{/key}
{:else if props.can_edit && !state.loading}
<div class="src-add">
<ui-button icon="plus" label="Add a source" onclick="fez.add()"></ui-button>
</div>
{/if}

<div class="dialog-actions">
<ui-button variant="primary" label="Done" onclick="fez.done()"></ui-button>
</div>
{/if}`
};
}

Fez('mail-sources-dialog', __fez_component_0);

export default __fez_component_0;
